import { useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { signOut } from 'next-auth/react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { isNotFoundApiError } from '@omnea-app/frontend/utils/error-handling/isApiErrorOfType'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

const useGetUserByEmailQuery = (email?: string) => {
  const { getToken } = useAuthenticatedUser()
  return {
    queryKey: ['getUserByEmail', email],
    queryFn: async () => {
      if (!email) return null

      try {
        const token = await getToken()
        const apiClient = new UsersHttpClient({ omneaJwt: token })
        const response = await apiClient.GET('/by-email/{email}', {
          params: { path: { email } },
        })

        if ('error' in response && !('data' in response)) {
          throw response.error instanceof Error ? response.error : new Error(JSON.stringify(response.error))
        }
        return response.data
      } catch (error) {
        if (isNotFoundApiError(error)) {
          if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            await signOut({ callbackUrl: '/login' })
          }
          return null
        }

        throw error
      }
    },
  }
}

export const useGetUserByEmailSuspense = (email?: string) => {
  return useSuspenseQuery({
    ...useGetUserByEmailQuery(email),
    initialData: !email ? null : undefined, // Prevents component from suspending if email is not provided
    retry: (_, error) => !isNotFoundApiError(error),
  })
}

export const useGetUserByEmail = (email?: string) => {
  return useQuery({
    ...useGetUserByEmailQuery(email),
  })
}
